import moment from 'moment';
import {
  HesiExamInfoDto,
  RaasAssessmentMetricsDto,
  RaasAssessmentSourceDto,
  RecommendationAttemptStatusDto,
  RemediationActivityTypeDto,
  RemediationActivityTypeSettings,
  RemediationAssignmentWithSourcesDto,
  RecommendationContentTypeDto
} from '../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { Product } from '../constants/product.constants';
import { getProduct } from './product.utilities';

export enum PageContextType {
  HOME = 'HOME',
  OSMOSIS_VIDEO = 'OSMOSIS_VIDEO',
  EBOOK_READING = 'EBOOK_READING',
  POST_PLAYER = 'POST_PLAYER',
  TOPIC_OVERVIEW = 'TOPIC_OVERVIEW'
}

export type OsmosisVideoPageContext = {
  videoTitle: string;
  contentId: string;
  topicName: string;
  transcript: string;
};

export type EbookReadingPageContext = {
  bookTitle: string;
  isbn: string;
  sectionTitle: string;
  chapterHierarchy: string;
  pageRange: string;
};

export type TopicOverviewPageContext = {
  topicOverviewTitle: string;
  contentId: string;
};

export type PostPlayerPageContext = {
  completedTitle: string;
  completedType: RecommendationContentTypeDto;
  status: RecommendationAttemptStatusDto;
  timeSpent: string;
  nextRecommendationTitle: string;
};

/**
 * Live home-page data injected into the home-page context so the chat can
 * describe what the student is actually seeing rather than every possible case.
 * All fields mirror exactly what the home page renders:
 * - statusDisplay: the top-right status line (e.g. "Due: ...", "Completed: ...")
 *   as produced by the DueDate/Completion header, or null when nothing is shown.
 * - recommendationLabel: the recommendation card heading actually displayed
 *   ("Not sure where to start?" / "Not sure what to do next?"), or null when
 *   no card is shown (PLP complete or no next recommendation).
 * - topRecommendationTitle: the title of the activity the card's "View" button
 *   opens, or null when no card is shown.
 * - timeSpent: the exact time-spent value shown in the top-right header
 *   (e.g. "0 min", "1 hr 20 min"), as produced by the same helpers the header
 *   uses, or null when not provided. Home-page only — off the home page the
 *   submission-level total is not computed, so this stays null there.
 */
export type HomePageContext = {
  statusDisplay: string;
  recommendationLabel: string;
  topRecommendationTitle: string;
  timeSpent: string;
};

/**
 * Maximum character limit for pageContext sent to Sherpath AI
 */
export const PAGE_CONTEXT_MAX_LENGTH = 20000;

/**
 * Truncates pageContext to maximum character limit if needed.
 * Adds a truncation notice to inform the LLM that content was cut short.
 * Logs the original length to console when truncation occurs.
 *
 * @param pageContext The full pageContext string
 * @returns Truncated pageContext if it exceeds the limit, otherwise the original
 */
export const truncatePageContext = (pageContext: string): string => {
  if (!pageContext || pageContext.length <= PAGE_CONTEXT_MAX_LENGTH) {
    return pageContext;
  }

  const originalLength = pageContext.length;
  const truncationNotice = '\n\n---\n[NOTE: The full context was truncated due to length limitations. ' +
    'The information above represents the most important context available, but some details may have been omitted.]';

  // Reserve space for the truncation notice
  const maxContentLength = PAGE_CONTEXT_MAX_LENGTH - truncationNotice.length;
  const truncatedContext = pageContext.substring(0, maxContentLength) + truncationNotice;

  // Log to console when truncation occurs
  // eslint-disable-next-line no-console
  console.log(`[PageContext] Context truncated: original length ${originalLength} characters, truncated to ${PAGE_CONTEXT_MAX_LENGTH} characters`);

  return truncatedContext;
};

/**
 * Builds context string for the home page.
 *
 * When live home-page data is provided, the context names the actual status line,
 * recommendation-card label, and recommended activity the student is seeing,
 * rather than describing every possible case in prose.
 */
export const buildHomePageContext = (homeData?: Partial<HomePageContext>): string => {
  const statusDisplay = homeData?.statusDisplay?.trim();
  const recommendationLabel = homeData?.recommendationLabel?.trim();
  const topRecommendationTitle = homeData?.topRecommendationTitle?.trim();
  const timeSpent = homeData?.timeSpent?.trim();

  // The time spent shown in the top-right header. When provided this is the exact
  // on-screen value (including "0 min"), so the chat can report it directly rather
  // than disclaiming access. When absent, fall back to pointing at the header.
  const timeSpentSentence = timeSpent
    ? `The time spent shown in that header is currently "${timeSpent}", which you can share directly if the student asks.`
    : 'Time spent is not included in your context, so point the student to the time spent shown in that same header rather than guessing a value.';

  let contextString = '\n\n---\nCURRENT PAGE CONTEXT (what the student is actively viewing RIGHT NOW):\n' +
    'The student is currently on the main Personalized Learning Plan (PLP) home page. ' +
    'They can see all their assigned activities, overall progress, and requirements listed above. ' +
    'They are not currently engaged in a specific learning activity.';

  // Status / due-date / completion header (top right of the home page).
  // statusDisplay is the exact line the header shows; it may be empty when the
  // PLP has no due date and is not yet complete (the header shows no status line then).
  contextString += '\n\nThe top right of the Personalized Learning Plan (PLP) home page shows the student\'s completion percentage and time spent, ';
  if (statusDisplay) {
    contextString += `along with this status line: "${statusDisplay}". ` +
      'If the student asks about their due date, completion date, or overall completion, answer using these values directly. ' +
      timeSpentSentence;
  } else {
    contextString += 'and this PLP currently has no due date or completion date shown there. ' +
      'If the student asks about their due date or completion date, let them know none is set for this PLP. ' +
      'Overall completion is available in the context above and can be shared directly. ' +
      timeSpentSentence;
  }

  // Recommendation card. It is only shown while the PLP is incomplete and a next
  // recommendation exists; recommendationLabel/topRecommendationTitle are empty otherwise.
  if (recommendationLabel && topRecommendationTitle) {
    contextString += `\n\nNear the top of the home page is a recommendation card labeled "${recommendationLabel}" ` +
      `whose "View" button opens the student's top recommended activity, which is currently "${topRecommendationTitle}". ` +
      'If the student asks what they should do next or where to start, point them to that card and activity.';
  } else {
    contextString += '\n\nThe home page normally shows a recommendation card whose "View" button opens the student\'s top recommended activity, ' +
      'but it is not currently shown (the PLP is complete or has no remaining recommended activity). ' +
      'If the student asks what to do next, work from the requirements and progress in the context above rather than pointing to the card.';
  }

  // Page tour (home page only).
  contextString += '\n\nThe primary way to navigate or find things on this page is the "Take a tour of this page" link at the top of the home page, ' +
    'which gives a guided walkthrough. The home page is the launch point for the whole PLP, and the tour covers what it contains: ' +
    'the completion, time spent, and status header in the top right; ' +
    'the Review, Apply, and Assess sections with their assigned activities; and the recommendation card. ' +
    'So for a student asking how to find, reach, or open any of those — quiz questions, assignments, activities, their progress — the "Take a tour of this page" link is the relevant pointer, along with the specific location above when one is known. ' +
    'The tour does not cover the learning content itself.';

  return contextString;
};

/**
 * Builds context string for Osmosis video page
 */
export const buildOsmosisVideoContext = (videoData: OsmosisVideoPageContext): string => {
  let contextString = '\n\n---\nCURRENT PAGE CONTEXT (what the student is actively viewing RIGHT NOW):\n';
  contextString += `The student is currently watching an Osmosis video as part of their Review requirements.\n\n`;
  contextString += `Active Video Details:\n`;
  contextString += `- Topic: ${videoData.topicName || 'assigned topic'}\n`;
  contextString += `- Title: ${videoData.videoTitle}\n`;
  contextString += `- Content ID: ${videoData.contentId}\n`;

  if (videoData.transcript) {
    contextString += `\nFull Video Transcript (use this to answer specific questions about the video content):\n${videoData.transcript}`;
  } else {
    contextString += `\nNote: The video transcript is not yet available. The student is actively watching the video.`;
  }

  return contextString;
};

/**
 * Builds context string for eBook reading page
 */
export const buildEbookReadingContext = (ebookData: EbookReadingPageContext): string => {
  let contextString = '\n\n---\nCURRENT PAGE CONTEXT (what the student is actively viewing RIGHT NOW):\n';
  contextString += `The student is currently reading from an eBook as part of their Review requirements.\n\n`;
  contextString += `Active Reading Details:\n`;
  contextString += `- Book: ${ebookData.bookTitle}\n`;
  contextString += `- ISBN: ${ebookData.isbn}\n`;

  if (ebookData.sectionTitle) {
    contextString += `- Current Section: ${ebookData.sectionTitle}\n`;
  }
  if (ebookData.chapterHierarchy) {
    contextString += `- Chapter Path: ${ebookData.chapterHierarchy}\n`;
  }

  contextString += `- Page Range: ${ebookData.pageRange}\n\n`;
  contextString += `Note: You cannot yet read the full text being viewed on the page.  You can however still search this and other books based on the user message.  If asked for a summary make it clear you cannot yet read the current page but you can search this and other books based on the current page context or the user's message. The current page context describes the reading the student is currently viewing.  Answers can only come from what is in the current page context as well as search results from this book and other books in the same domain area.  If the student asks about a specific image, chart, or diagram on the current page, let them know you can only read the text and cannot currently access images, charts, or diagrams.  However, you can still search for related images and videos when prompted.`;

  return contextString;
};

/**
 * Builds context string for Topic Overview page
 */
export const buildTopicOverviewContext = (topicOverviewData: TopicOverviewPageContext): string => {
  let contextString = '\n\n---\nCURRENT PAGE CONTEXT (what the student is actively viewing RIGHT NOW):\n';
  contextString += `The student is currently viewing a Topic Overview as part of their Review requirements.\n\n`;
  contextString += `Active Topic Overview Details:\n`;
  contextString += `- Title: ${topicOverviewData.topicOverviewTitle}\n`;
  contextString += `- Content ID: ${topicOverviewData.contentId}\n\n`;
  contextString += `Note: You cannot read the text of the Topic Overview being viewed on the page. You can however still search for content related to the Topic Overview title and the user's message. If asked for a summary make it clear you cannot read the current Topic Overview but you can search for related content based on its title or the user's message.`;

  return contextString;
};

/**
 * Builds context string for post-player completion page
 */
export const buildPostPlayerContext = (postPlayerData: PostPlayerPageContext): string => {
  let contextString = '\n\n---\nCURRENT PAGE CONTEXT (what the student is actively viewing RIGHT NOW):\n';

  // Determine completion state based on status
  const { status } = postPlayerData;

  if (status === RecommendationAttemptStatusDto.COMPLETED_PASSED) {
    contextString += `The student successfully completed a PLP activity and is viewing their completion status.\n\n`;
  } else if (status === RecommendationAttemptStatusDto.COMPLETED_FAILED) {
    contextString += `The student completed a PLP activity but did not meet the passing threshold, and is viewing their completion status.\n\n`;
  } else if (status === RecommendationAttemptStatusDto.IN_PROGRESS) {
    contextString += `The student returned from a PLP activity without completing it, and is viewing the activity status.\n\n`;
  }

  contextString += `Activity:\n`;
  contextString += `- Activity Type: ${postPlayerData.completedType}\n`;
  contextString += `- Activity Title: ${postPlayerData.completedTitle}\n`;

  // Show appropriate result based on status
  if (status === RecommendationAttemptStatusDto.IN_PROGRESS) {
    contextString += `- Status: In progress (closed before finishing)\n`;
  } else if (status === RecommendationAttemptStatusDto.COMPLETED_PASSED) {
    contextString += `- Status: Completed and passed\n`;
  } else if (status === RecommendationAttemptStatusDto.COMPLETED_FAILED) {
    contextString += `- Status: Completed with not passing score (needs to take another attempt)\n`;
  }

  if (postPlayerData.timeSpent) {
    contextString += `- Time Spent: ${postPlayerData.timeSpent}\n`;
  }
  if (postPlayerData.nextRecommendationTitle) {
    contextString += `\nNext Recommendation:\n`;
    contextString += `- ${postPlayerData.nextRecommendationTitle}\n`;
  }

  contextString += `\nNote: The student may ask questions about what they just completed/exited or what to do next.`;

  return contextString;
};

/**
 * Builds page-specific context string to append to base PLP context
 */
export const buildPageSpecificContext = (
  pageType: PageContextType,
  pageData: OsmosisVideoPageContext | EbookReadingPageContext | PostPlayerPageContext | TopicOverviewPageContext,
  homePageData?: Partial<HomePageContext>
): string => {
  if (!pageData || pageType === PageContextType.HOME) {
    return buildHomePageContext(homePageData);
  }

  switch (pageType) {
    case PageContextType.OSMOSIS_VIDEO:
      return buildOsmosisVideoContext(pageData as OsmosisVideoPageContext);
    case PageContextType.EBOOK_READING:
      return buildEbookReadingContext(pageData as EbookReadingPageContext);
    case PageContextType.POST_PLAYER:
      return buildPostPlayerContext(pageData as PostPlayerPageContext);
    case PageContextType.TOPIC_OVERVIEW:
      return buildTopicOverviewContext(pageData as TopicOverviewPageContext);
    default:
      return '';
  }
};

export const getExamName = (hesiExamInfo: HesiExamInfoDto): string => {
  if (!hesiExamInfo) {
    return '';
  }

  return [hesiExamInfo.programType, hesiExamInfo.examType]
    .filter((i) => i)
    .join(' ');
};

/**
 * Builds the pageContext string for Sherpath AI that provides context about the student's
 * Personalized Learning Plan (PLP), including exam information, requirements, and progress.
 */
export const buildSherpathAIPageContext = (props: {
  remediationAssignmentWithSources: RemediationAssignmentWithSourcesDto;
  assessmentMetrics: RaasAssessmentMetricsDto;
  assessmentSources: RaasAssessmentSourceDto[];
  hesiExamInfo: HesiExamInfoDto;
  totalProgressPercent: number;
  pageType: PageContextType;
  pageSpecificData: OsmosisVideoPageContext | EbookReadingPageContext | PostPlayerPageContext | TopicOverviewPageContext;
  homePageData?: Partial<HomePageContext>;
}): string => {
  const {
    remediationAssignmentWithSources,
    assessmentMetrics,
    assessmentSources,
    hesiExamInfo,
  } = props;

  if (!remediationAssignmentWithSources) {
    return '';
  }

  // The base context below is HESI-specific (HESI exam, HESI Support), so Spark sends only the page context.
  if (getProduct(remediationAssignmentWithSources) === Product.SPARK) {
    return truncatePageContext(buildPageSpecificContext(
      props.pageType || PageContextType.HOME,
      props.pageSpecificData || null,
      props.homePageData
    ).trim());
  }

  const { eolsAssignment, remediationAssignment } = remediationAssignmentWithSources;

  // Get exam information
  const examName = getExamName(hesiExamInfo) || '<exam-name>';
  const examScore = assessmentSources?.[0]?.score
    ? Math.round(assessmentSources[0].score).toString()
    : '<exam-score>';
  const scoreBenchmark = hesiExamInfo?.recommendedScore || '<score-benchmark>';

  // Get active activity type settings
  const activeSettings: RemediationActivityTypeSettings[] = remediationAssignment.settings.activityTypes
    ? remediationAssignment.settings.activityTypes.filter((activityType: RemediationActivityTypeSettings) => activityType.isActive)
    : [];

  // Build review requirements
  const reviewSettings = activeSettings.find(setting => setting.activityType === RemediationActivityTypeDto.REVIEW_BY_TAXON);

  let reviewRequirements = '';
  if (reviewSettings && assessmentMetrics?.assessmentGoalMetrics) {
    // Filter to only include REVIEW_BY_TAXON activity types
    const reviewGoals = assessmentMetrics.assessmentGoalMetrics
      .filter(goalMetric => goalMetric.assessmentGoal.type === RemediationActivityTypeDto.REVIEW_BY_TAXON)
      .map(goalMetric => {
        const totalRequired = goalMetric.metrics.activityTypeTotalGoal;
        const topicName = goalMetric.assessmentGoal.text || 'Unknown Topic';
        return `  - ${totalRequired} readings or videos on the topic "${topicName}"`;
      });
    reviewRequirements = reviewGoals.join('\n');
  }

  // Build apply requirements
  const applySettings = activeSettings.find(setting => setting.activityType === RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE);

  let applyRequirements = '';
  if (applySettings && assessmentMetrics?.applyContent) {
    const totalGoal = assessmentMetrics.applyContent.activityTypeTotalGoal;
    // minScoreToPass is stored as a decimal (e.g., 0.8 = 80%), so multiply by 100 to get percentage
    const minScoreToPassDecimal = applySettings.contentSettings?.[0]?.minScoreToPass;

    if (minScoreToPassDecimal != null) {
      const minScoreToPass = Math.round(minScoreToPassDecimal * 100);
      applyRequirements = `  - Complete ${totalGoal} case studies, each with a score of ${minScoreToPass}% or higher`;
    } else {
      applyRequirements = `  - Complete ${totalGoal} case studies`;
    }
  }

  // Build assess requirements
  const assessSettings = activeSettings.find(setting => setting.activityType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE);

  let assessRequirements = '';
  if (assessSettings && assessmentMetrics?.assessContent) {
    const totalGoal = assessmentMetrics.assessContent.activityTypeTotalGoal;
    const attemptGoal = assessmentMetrics.assessContent.activityTypeAttemptGoal;
    // minScoreToPass is stored as a decimal (e.g., 0.8 = 80%), so multiply by totalGoal directly
    const minScoreToPassDecimal = assessSettings.contentSettings?.[0]?.minScoreToPass;

    if (minScoreToPassDecimal != null) {
      const minCorrect = Math.ceil(minScoreToPassDecimal * attemptGoal);
      assessRequirements = `  - Attempt all ${attemptGoal} quiz questions and answer at least ${minCorrect} correctly`;
    } else {
      assessRequirements = `  - Attempt all ${attemptGoal} quiz questions and answer at least ${totalGoal} correctly`;
    }
  }

  // Format due date. When there is no due date we must NOT emit a placeholder
  // token (a bare "<date-time>" sitting next to the real current date leads the
  // model to fabricate a due date, often today's date). Instead, branch below and
  // state explicitly that no due date is set.
  const hasDueDate = Boolean(eolsAssignment?.dueDate);
  const dueDate = hasDueDate
    ? moment(eolsAssignment.dueDate).format('MMMM D, YYYY [at] h:mm A')
    : '';

  // Format current date
  const currentDate = moment().format('MMMM D, YYYY [at] h:mm A');

  // The due-date line for the "Timing and overall progress" block. Explicit
  // "no due date" wording when absent so the model never guesses one.
  const dueDateLine = hasDueDate
    ? `- Due date: ${dueDate}`
    : '- Due date: This PLP has no due date set (once the PLP is complete this is instead shown as its completion date).';

  // The whole-integer completion percent the student sees in the top-right header,
  // computed by getTotalProgress and passed in by the caller. It is deliberately NOT
  // recomputed from assessmentMetrics.totalProgress here: getTotalProgress floors that
  // decimal and applies the all-goals-zero -> 100% override, so recomputing would let
  // the chat report a number that contradicts the header the student is looking at.
  const overallCompletion = props.totalProgressPercent ?? 0;

  // Get section progress
  const getActivityProgress = (activityMetrics: typeof assessmentMetrics.reviewContent) => {
    if (!activityMetrics) return { completed: 0, total: 0 };
    return {
      completed: activityMetrics.activityTypeTotalGoalCompleted || 0,
      total: activityMetrics.activityTypeTotalGoal || 0
    };
  };

  const reviewProgress = getActivityProgress(assessmentMetrics?.reviewContent || null);
  const applyProgress = getActivityProgress(assessmentMetrics?.applyContent || null);

  // For assess content, get questions attempted and answered correctly
  const assessQuestionsAttempted = assessmentMetrics?.assessContent?.activityTypeAttemptGoalCompleted || 0;
  const assessAttemptGoal = assessmentMetrics?.assessContent?.activityTypeAttemptGoal || 0;
  const assessQuestionsCorrect = assessmentMetrics?.assessContent?.activityTypeTotalGoalCompleted || 0;
  const assessCorrectGoal = assessmentMetrics?.assessContent?.activityTypeTotalGoal || 0;

  // Build the full context string
  const contextString = `You are embedded within a student's Personalized Learning Plan (PLP).

You can share any of the following PLP information with the student as they also have access to this data.

A PLP is generated after a student completes a HESI exam. Its purpose is to help the student improve in specific areas where they need additional focus based on their exam performance.

Student HESI exam context:
- Exam: ${examName}
- Student score: ${examScore}
- Recommended benchmark score: ${scoreBenchmark}

The PLP is personalized using the student's exam results and focuses on topics related to questions they answered incorrectly. The assigned content is directly relevant to the student's incorrectly answered questions and knowledge gaps.

This student's PLP includes the following requirements:
- Review:
${reviewRequirements || '  - (No review requirements)'}
- Apply:
${applyRequirements || '  - (No apply requirements)'}
- Assess:
${assessRequirements || '  - (No assess requirements)'}

Additional notes:
- Each review topic may include more readings or videos than required. Students may choose which items to complete and only need to complete the required number. Completing additional items is optional.
- The listed review topics represent areas where the student needs improvement based on their incorrectly answered exam questions.
- Faculty can see the student's PLP progress throughout — both before and after the due date. This visibility is not tied to the due date: faculty can view progress the whole time, and the due date does not turn that on or off. The student can continue working on the PLP after the due date; that later work, and the fact that it was completed after the due date, is part of the same progress faculty can already see.
- Students are not required to complete PLP activities in a fixed order. However, the interface may recommend a next step based on the student's current progress. Any page-specific recommendation will be provided in PAGE_CONTEXT.
- The Personalized Learning Plan (PLP) home page is the hub for the whole plan. Its top right shows a status header with the PLP's due date (replaced by the completion date once the PLP is finished; some PLPs have neither), overall completion, and time spent. The home page also holds the Review, Apply, and Assess sections with their assigned activities, and a recommendation card for the next activity. At the top of the home page there is also a "Take a tour of this page" link that gives a guided walkthrough of where all of those things are. Whichever page the student is on, the live values for those metrics are on the home page, and that tour is a home-page feature — it is not present on a reading, video, or other activity page. So when a student anywhere asks where something is or how to find or navigate to it, the home page (and its "Take a tour of this page" link) is the place that answers that; if they are currently in an activity, that means returning to the home page first (see the return path below).
- While inside an activity such as an eBook reading, video, case study, or quiz, the student leaves using the Close (X) control in the top-left of the activity's page header. Closing (and confirming) does not jump straight to the home page — it takes the student to the activity's completion page, and the "Back to plan" button lives there, on that completion page, to finish returning to the home page. So getting home from an activity is a two-step path: Close the activity to reach the completion page, then use "Back to plan" from there. The "Back to plan" button is not present during the reading or video itself, so it is not the control to name while the student is still in the activity.
- This chat is unavailable while students access their HESI Case Study and Quizzing pages
- If the student asks how long their PLP or an activity will take, do not invent or add up a single overall time estimate for the whole plan — that would contradict the per-activity estimates the content shows. Instead, point them to those per-activity estimates: an activity shows an estimated time (e.g. "est. 15 min") on its card when one is available. Not every activity has an estimate, and one is shown only when available, so do not guess, infer, or state a time for an activity that does not show one — tell the student the estimate appears on the activity's card when there is one. You can also describe how much work is left using the section counts below (for example, how many of the required readings/videos, case studies, or quiz questions remain), framed as remaining items rather than a total amount of time.

When responding:
- Be polite, supportive, and encouraging. Students may feel overwhelmed while completing their PLPs.
- Help the student improve their understanding and confidence in these topics.
- If the student asks why they were assigned certain topics, explain that these topics were identified as areas needing improvement based on their exam results.
- If the student asks about specific HESI exam questions, including which ones they got correct or incorrect, explain that Sherpath AI does not have access to the exact exam questions or the student's response-level exam history for security reasons.
- If context is missing or unclear, ask a clarifying question instead of assuming.
- Only reference student progress, requirements, topics, or recommendations that are explicitly provided in the injected context.
- Only mention the student's HESI exam score or the recommended benchmark score if the student explicitly asks about their score. Do not volunteer this information unprompted.
- HESI Support handles questions this chat cannot resolve — login or access issues, billing, technical problems, and exam administration. Support is reachable through the "HESI Support Live Chat" button in the bottom right of the main HESI pages (the student returns to the Personalized Learning Plan (PLP) home page and then exits the PLP to reach them) or by phone at (800) 222-9570.
- The due date and overall completion above can be shared directly if the student asks, and these — along with time spent — are shown to the student in the top right of the Personalized Learning Plan (PLP) home page, so name that location when answering about any of them, wherever the student currently is. Time spent itself is not among the values above unless it has been explicitly provided to you in CURRENT PAGE CONTEXT (home page only); otherwise it is only on that home-page header, so it is not a value to guess at.

Timing and overall progress:
${dueDateLine}
- Today's date (for your reference only, not the due date): ${currentDate}
- Overall completion: ${overallCompletion}%
- The student sees the due date (shown as the completion date once the PLP is finished), overall completion, and time spent together in the top right of the Personalized Learning Plan (PLP) home page. Name that location — the top right of the PLP home page — when answering about any of them, wherever the student currently is; from a reading, video, or other activity page that header is not visible until they return to the home page. Time spent is not among the values above; it may be present in CURRENT PAGE CONTEXT on the home page when explicitly provided there, otherwise it is only ever available to the student from that header.

Section progress:
- Review: ${reviewProgress.completed}/${reviewProgress.total} completed
- Apply: ${applyProgress.completed}/${applyProgress.total} completed
- Assess: ${assessQuestionsAttempted} questions attempted (goal: ${assessAttemptGoal}), ${assessQuestionsCorrect} answered correctly (goal: ${assessCorrectGoal}). These are counts against a goal, not a fraction of one another — a count that meets or exceeds its goal means the student has met or surpassed that goal.`;

  // Append page-specific context if provided
  const pageContext = buildPageSpecificContext(
    props.pageType || PageContextType.HOME,
    props.pageSpecificData || null,
    props.homePageData
  );

  const fullContext = contextString + pageContext;

  // Truncate if exceeds maximum length
  return truncatePageContext(fullContext);
};
